import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useStorage } from '../../hooks/useStorage'
import { deleteFile, extractPublicIdFromUrl } from '../../services/storage'
import './ImageUploader.css'

/**
 * ImageUploader - Drag-and-drop image upload with preview
 * 
 * @param {string|array} value - Current image URL(s)
 * @param {function} onChange - Change handler
 * @param {string} entityType - For storage path organization
 * @param {string} accent - 'cyan' | 'amber'
 * @param {boolean} multiple - Allow multiple images
 */
export function ImageUploader({
  value,
  onChange,
  entityType = 'general',
  accent = 'cyan',
  multiple = false,
}) {
  const [isDragging, setIsDragging] = useState(false)
  const [previewImages, setPreviewImages] = useState([])
  const [uploadingFiles, setUploadingFiles] = useState([])
  const fileInputRef = useRef(null)
  const { uploadForEntity, progress, uploading, error } = useStorage()

  // Normalize value to array for consistent handling
  const currentImages = Array.isArray(value) 
    ? value 
    : value ? [value] : []

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    )
    
    if (files.length > 0) {
      handleFiles(multiple ? files : [files[0]])
    }
  }

  // Handle file input change
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      handleFiles(multiple ? files : [files[0]])
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  // Process selected files
  const handleFiles = useCallback(async (files) => {
    // Create local previews
    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9),
      prompt: '',
      model: '',
      notes: '',
    }))

    if (multiple) {
      setPreviewImages(prev => [...prev, ...newPreviews])
    } else {
      // Clean up old preview URLs
      previewImages.forEach(p => URL.revokeObjectURL(p.preview))
      setPreviewImages(newPreviews)
    }

    // Upload files
    setUploadingFiles(newPreviews.map(p => p.id))
    
    for (const preview of newPreviews) {
      try {
        const result = await uploadForEntity(preview.file, entityType)
        
        // Update preview with uploaded URL and publicId
        setPreviewImages(prev => 
          prev.map(p => 
            p.id === preview.id 
              ? { ...p, url: result.url, path: result.path, publicId: result.publicId } 
              : p
          )
        )
        
        setUploadingFiles(prev => prev.filter(id => id !== preview.id))
        
        // Notify parent of change (include publicId for deletion capability)
        if (multiple) {
          setPreviewImages(prev => {
            const updated = prev.map(p => 
              p.id === preview.id 
                ? { ...p, url: result.url, path: result.path, publicId: result.publicId } 
                : p
            )
            onChange(updated.filter(p => p.url))
            return updated
          })
        } else {
          onChange({ url: result.url, path: result.path, publicId: result.publicId })
        }
      } catch (err) {
        console.error('Upload failed:', err)
        setUploadingFiles(prev => prev.filter(id => id !== preview.id))
      }
    }
  }, [entityType, multiple, onChange, uploadForEntity, previewImages])

  // Remove an image (also deletes from Cloudinary)
  const handleRemove = async (imageToRemove) => {
    if (imageToRemove.preview && imageToRemove.preview.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.preview)
    }
    
    // Delete from Cloudinary if we have a publicId or URL
    const publicId = imageToRemove.publicId || extractPublicIdFromUrl(imageToRemove.url)
    if (publicId) {
      // Fire and forget - don't block UI for deletion
      deleteFile(publicId).then(result => {
        if (result.success) {
          console.log('Image deleted from Cloudinary:', publicId)
        }
      }).catch(err => {
        console.warn('Failed to delete image from Cloudinary:', err)
      })
    }
    
    if (multiple) {
      const updated = previewImages.filter(img => 
        img.id !== imageToRemove.id && img.url !== imageToRemove.url
      )
      setPreviewImages(updated)
      onChange(updated.filter(p => p.url))
    } else {
      setPreviewImages([])
      onChange(null)
    }
  }

  // Update image metadata
  const handleMetadataChange = (imageId, field, value) => {
    setPreviewImages(prev => {
      const updated = prev.map(img =>
        img.id === imageId ? { ...img, [field]: value } : img
      )
      if (multiple) {
        onChange(updated.filter(p => p.url))
      } else if (updated[0]?.url) {
        onChange(updated[0])
      }
      return updated
    })
  }

  // Click to browse
  const handleClick = () => {
    fileInputRef.current?.click()
  }

  // Animation variants
  const dropzoneVariants = {
    idle: { scale: 1 },
    dragging: { 
      scale: 1.02,
      transition: { type: 'spring', stiffness: 300, damping: 20 }
    },
  }

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 25 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { duration: 0.15 }
    },
  }

  // Display images (current + new previews)
  // Handle both legacy string URLs and new object format { url, publicId }
  const displayImages = previewImages.length > 0 
    ? previewImages 
    : currentImages.map((img, i) => ({ 
        id: `existing-${i}`, 
        url: typeof img === 'string' ? img : img?.url,
        preview: typeof img === 'string' ? img : img?.url,
        publicId: typeof img === 'object' ? img?.publicId : null,
        path: typeof img === 'object' ? img?.path : null,
      }))

  return (
    <div className={`image-uploader image-uploader--${accent}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileChange}
        className="image-uploader__input"
      />

      {/* Drop zone */}
      {(multiple || displayImages.length === 0) && (
        <motion.div
          className={`image-uploader__dropzone ${isDragging ? 'image-uploader__dropzone--active' : ''}`}
          variants={dropzoneVariants}
          animate={isDragging ? 'dragging' : 'idle'}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className="image-uploader__dropzone-content">
            <svg 
              className="image-uploader__icon" 
              width="48" 
              height="48" 
              viewBox="0 0 48 48" 
              fill="none"
            >
              <path 
                d="M42 30V38C42 39.0609 41.5786 40.0783 40.8284 40.8284C40.0783 41.5786 39.0609 42 38 42H10C8.93913 42 7.92172 41.5786 7.17157 40.8284C6.42143 40.0783 6 39.0609 6 38V30" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M34 16L24 6L14 16" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <path 
                d="M24 6V30" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <p className="image-uploader__text">
              {isDragging 
                ? 'Drop image here' 
                : multiple 
                  ? 'Drag & drop images or click to browse'
                  : 'Drag & drop an image or click to browse'
              }
            </p>
            <span className="image-uploader__hint">
              JPEG, PNG, GIF, WebP (max 10MB)
            </span>
          </div>
        </motion.div>
      )}

      {/* Progress bar */}
      {uploading && (
        <div className="image-uploader__progress">
          <div 
            className="image-uploader__progress-bar"
            style={{ width: `${progress}%` }}
          />
          <span className="image-uploader__progress-text">{progress}%</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="image-uploader__error">
          {error}
        </div>
      )}

      {/* Image previews */}
      <AnimatePresence mode="popLayout">
        {displayImages.length > 0 && (
          <div className={`image-uploader__previews ${multiple ? 'image-uploader__previews--grid' : ''}`}>
            {displayImages.map((image) => (
              <motion.div
                key={image.id || image.url}
                className="image-uploader__preview"
                variants={imageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                <div className="image-uploader__preview-image-wrapper">
                  <img 
                    src={image.preview || image.url} 
                    alt="Preview"
                    className="image-uploader__preview-image"
                  />
                  {uploadingFiles.includes(image.id) && (
                    <div className="image-uploader__preview-loading">
                      <span className="image-uploader__spinner" />
                    </div>
                  )}
                  <button
                    type="button"
                    className="image-uploader__preview-remove"
                    onClick={() => handleRemove(image)}
                    aria-label="Remove image"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path 
                        d="M12 4L4 12M4 4L12 12" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Image metadata fields */}
                <div className="image-uploader__preview-meta">
                  <input
                    type="text"
                    className="image-uploader__meta-input"
                    placeholder="Prompt used to generate..."
                    value={image.prompt || ''}
                    onChange={(e) => handleMetadataChange(image.id, 'prompt', e.target.value)}
                  />
                  <div className="image-uploader__meta-row">
                    <input
                      type="text"
                      className="image-uploader__meta-input image-uploader__meta-input--small"
                      placeholder="Model"
                      value={image.model || ''}
                      onChange={(e) => handleMetadataChange(image.id, 'model', e.target.value)}
                    />
                    <input
                      type="text"
                      className="image-uploader__meta-input image-uploader__meta-input--small"
                      placeholder="Notes"
                      value={image.notes || ''}
                      onChange={(e) => handleMetadataChange(image.id, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ImageUploader

